import { useState } from 'react';
import { App, Layout, Menu, Flex, Typography, Dropdown, Badge, Button } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useStoreContext } from '@/shared/lib/store-context';
import { useGetMeQuery, useLogoutMutation } from '@/entities/user';
import { useOrderRequestCountQuery } from '@/entities/order-request';
import { useJoinRequestCountQuery } from '@/entities/join-request';
import { ownerPath, ROUTES } from '@/shared/config/routes';
import { WindowManagerProvider } from '@/app/providers/WindowManagerProvider';
import { HotkeyProvider, useActiveBindings, useHotkeyActions, useHotkeyScope, type ActionImplMap, type HotkeyBinding } from '@/shared/lib/hotkeys';
import { useWindowManager } from '@/shared/lib/windows';
import { ComboKbd, HotkeysHelpDialog, HotkeyTip } from '@/shared/ui/hotkey';
import { ALL_NAV, DOCUMENT_NAV, REFERENCE_NAV, REPORT_NAV, REQUEST_NAV, SINGLE_NAV, type NavLeaf } from './ownerNav';

const { Sider, Header, Content } = Layout;

const PARENT_KEYS = ['references', 'documents', 'requests', 'reports'];

/** §1: владельческий раздел — плотный сайдбар; порядок разделов внутри групп — по ТЗ, группировка — UI-группировка для краткости списка. */
export function OwnerLayout() {
  return (
    <HotkeyProvider>
      <WindowManagerProvider>
        <OwnerLayoutContent />
      </WindowManagerProvider>
    </HotkeyProvider>
  );
}

/** Внутри провайдеров — иначе клик по «Отчётам» в сайдбаре (открывает плавающее окно, не
 *  страницу) не достучится до `useWindowManager()`. */
function OwnerLayoutContent() {
  const { modal } = App.useApp();
  const ctx = useStoreContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [logout] = useLogoutMutation();
  const { data: me } = useGetMeQuery();
  const windowManager = useWindowManager();
  const { data: orderRequestCount } = useOrderRequestCountQuery(undefined, { pollingInterval: 60_000 });
  const { data: joinRequestCount } = useJoinRequestCountQuery(undefined, { pollingInterval: 60_000 });
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  // Справка F1: снимок того, что работало там, где нажали (после открытия диалога фокус уже в нём).
  const activeBindings = useActiveBindings();
  const [help, setHelp] = useState<{ open: boolean; bindings: HotkeyBinding[] }>({ open: false, bindings: [] });

  const keyOf = (leaf: NavLeaf) => ownerPath(ctx.storeId, leaf.path);
  const leafItem = (leaf: NavLeaf, label: React.ReactNode = leaf.label) => ({
    key: keyOf(leaf),
    label,
    // Клавиша перехода справа в пункте; длинную подпись можно прочитать в нативной подсказке.
    title: leaf.label,
    extra: <ComboKbd id={leaf.action} />,
  });
  const badged = (text: string, count: number | undefined) => (
    <Flex align="center" gap={6}>
      {text}
      {count && count > 0 ? <Badge count={count} size="small" /> : null}
    </Flex>
  );

  const requestBadgeTotal = (orderRequestCount?.new ?? 0) + (joinRequestCount?.pending ?? 0);

  const items = [
    { key: 'references', label: 'Справочники', children: REFERENCE_NAV.map((l) => leafItem(l)) },
    { key: 'documents', label: 'Документы', children: DOCUMENT_NAV.map((l) => leafItem(l)) },
    {
      key: 'requests',
      label: badged('Заявки', requestBadgeTotal),
      children: REQUEST_NAV.map((l) => leafItem(l, badged(l.label, l.action === 'nav.orderRequests' ? orderRequestCount?.new : joinRequestCount?.pending))),
    },
    { key: 'reports', label: 'Отчёты', children: REPORT_NAV.map((l) => leafItem(l)) },
    ...SINGLE_NAV.map((l) => leafItem(l)),
  ];

  const selectedKey = ALL_NAV.map(keyOf).find((key) => location.pathname.startsWith(key));
  const groups: [string, NavLeaf[]][] = [
    ['references', REFERENCE_NAV],
    ['documents', DOCUMENT_NAV],
    ['requests', REQUEST_NAV],
    ['reports', REPORT_NAV],
  ];
  const defaultOpenKeys = groups.filter(([, leaves]) => leaves.some((l) => location.pathname.startsWith(keyOf(l)))).map(([key]) => key);

  function goTo(leaf: NavLeaf) {
    if (leaf.report) {
      windowManager.open({ kind: 'report', report: leaf.report });
      return;
    }
    void navigate(keyOf(leaf));
  }

  /** Выход спрашивает подтверждение, только если в открытых окнах есть несохранённое. */
  function handleLogout() {
    if (!windowManager.hasUnsavedChanges) {
      void logout();
      return;
    }
    modal.confirm({
      title: 'Выйти?',
      content: 'В открытых окнах есть несохранённые изменения — они будут потеряны.',
      okText: 'Выйти',
      cancelText: 'Остаться',
      okButtonProps: { danger: true },
      autoFocusButton: 'cancel',
      onOk: () => void logout(),
    });
  }

  // Глобальные клавиши интерфейса владельца: переходы по меню, новая продажа/приход, меню пользователя.
  const navImpl: ActionImplMap = Object.fromEntries(ALL_NAV.map((leaf) => [leaf.action, { hint: false, run: () => goTo(leaf) }]));
  const globalActions = useHotkeyActions({
    ...navImpl,
    'app.newSale': { hint: false, run: () => windowManager.open({ kind: 'document', type: 'SALE' }) },
    'app.newPurchase': { hint: false, run: () => windowManager.open({ kind: 'document', type: 'PURCHASE' }) },
    'app.userMenu': { hint: false, run: () => setUserMenuOpen(true) },
    'app.logout': { hint: false, run: handleLogout },
    'app.stores': { hint: false, run: () => void navigate(ROUTES.stores) },
    'app.help': { hint: false, run: () => setHelp({ open: true, bindings: activeBindings() }) },
  });
  useHotkeyScope(globalActions.bindings, true, { level: 'global' });

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider width={232} theme="light" style={{ borderRight: '1px solid #d9d9d9' }}>
        <Menu
          mode="inline"
          selectedKeys={selectedKey ? [selectedKey] : []}
          defaultOpenKeys={defaultOpenKeys}
          items={items}
          onClick={({ key }) => {
            if (PARENT_KEYS.includes(key)) return;
            const leaf = ALL_NAV.find((l) => keyOf(l) === key);
            if (leaf) goTo(leaf);
          }}
          style={{ borderInlineEnd: 'none' }}
        />
      </Sider>
      <Layout>
        <Header style={{ background: '#fff', borderBottom: '1px solid #d9d9d9', padding: '0 16px', height: 40, lineHeight: '40px' }}>
          <Flex justify="space-between" align="center">
            <Typography.Text strong>{ctx.storeName}</Typography.Text>
            <Flex gap={8} align="center">
              <HotkeyTip action={globalActions.get('app.stores')} placement="bottom" display="inline-flex">
                <Button type="link" size="small" onClick={() => void navigate(ROUTES.stores)}>
                  Мои магазины
                </Button>
              </HotkeyTip>
              <Dropdown
                trigger={['click']}
                open={userMenuOpen}
                onOpenChange={setUserMenuOpen}
                menu={{
                  items: [{ key: 'logout', label: 'Выйти', extra: <ComboKbd id="app.logout" />, onClick: handleLogout }],
                }}
              >
                <span style={{ display: 'inline-flex' }}>
                  <HotkeyTip action={globalActions.get('app.userMenu')} placement="bottom" display="inline-flex">
                    <Button type="text" size="small" icon={<UserOutlined />} aria-label="Меню пользователя">
                      {me?.name ?? 'Аккаунт'}
                    </Button>
                  </HotkeyTip>
                </span>
              </Dropdown>
            </Flex>
          </Flex>
        </Header>
        <Content style={{ padding: 12 }}>
          <Outlet />
        </Content>
      </Layout>
      <HotkeysHelpDialog open={help.open} bindings={help.bindings} onClose={() => setHelp((h) => ({ ...h, open: false }))} />
    </Layout>
  );
}
